/**
 * /app/guias-de-talles — CRUD de Guías de Talles (SCRUM-102).
 * Un recurso real de catálogo, reutilizable por múltiples productos,
 * opcional, tenant-scoped. Se asocia a un producto desde su editor
 * (app.productos.tsx), no exige salir de ahí para crear una nueva.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CrudShellHeader, DataTable, CrudEmptyState, Loading, Panel } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, X, Ruler } from "lucide-react";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";

export const Route = createFileRoute("/app/guias-de-talles")({
  component: SizeGuidesPage,
  head: () => ({ meta: [{ title: "Guías de talles — Structura Market" }] }),
});

interface SizeGuide {
  id: string;
  name: string;
  unit: string;
  content: { columns: string[]; rows: Array<{ values: string[] }> };
  notes: string | null;
  active: boolean;
  productCount?: number;
}

type FormState = {
  name: string;
  unit: string;
  notes: string;
  columns: string[];
  rows: string[][];
};

const EMPTY_FORM: FormState = {
  name: "",
  unit: "cm",
  notes: "",
  columns: ["Talle", "Pecho", "Largo"],
  rows: [["S", "", ""], ["M", "", ""], ["L", "", ""]],
};

function guideToForm(g: SizeGuide): FormState {
  return {
    name: g.name,
    unit: g.unit,
    notes: g.notes ?? "",
    columns: g.content.columns.length ? g.content.columns : [""],
    rows: g.content.rows.length ? g.content.rows.map((r) => [...r.values]) : [[""]],
  };
}

function SizeGuidesPage() {
  const tid = useWorkspaceTenantId();
  const [guides, setGuides] = useState<SizeGuide[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState<{ name: string; productCount: number } | null>(null);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const d = await apiGetJson<{ guides: SizeGuide[] }>("/api/size-guides", tid);
      setGuides(d.guides ?? []);
    } catch {
      toast.error("Error al cargar guías de talles");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(
    () => (search.trim() ? guides.filter((g) => g.name.toLowerCase().includes(search.trim().toLowerCase())) : guides),
    [guides, search],
  );

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (g: SizeGuide) => {
    setEditingId(g.id);
    setForm(guideToForm(g));
    setShowForm(true);
  };

  const addColumn = () => setForm((f) => ({ ...f, columns: [...f.columns, ""], rows: f.rows.map((r) => [...r, ""]) }));
  const removeColumn = (i: number) =>
    setForm((f) => ({ ...f, columns: f.columns.filter((_, idx) => idx !== i), rows: f.rows.map((r) => r.filter((_, idx) => idx !== i)) }));
  const addRow = () => setForm((f) => ({ ...f, rows: [...f.rows, f.columns.map(() => "")] }));
  const removeRow = (i: number) => setForm((f) => ({ ...f, rows: f.rows.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!tid) return;
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (form.columns.some((c) => !c.trim())) return toast.error("Ninguna columna puede estar vacía");
    const content = { columns: form.columns, rows: form.rows.map((r) => ({ values: r })) };
    setSaving(true);
    try {
      if (editingId) {
        await apiSendJson(`/api/size-guides?id=${editingId}`, "PATCH", { name: form.name, unit: form.unit, notes: form.notes || null, content }, tid);
        toast.success("Guía actualizada — se aplica a todos los productos que la usan");
      } else {
        await apiSendJson("/api/size-guides", "POST", { name: form.name, unit: form.unit, notes: form.notes || null, content }, tid);
        toast.success("Guía creada");
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (g: SizeGuide) => {
    if ((g.productCount ?? 0) > 0) {
      setDeleteBlocked({ name: g.name, productCount: g.productCount ?? 0 });
      return;
    }
    void doDelete(g.id);
  };

  const doDelete = async (id: string) => {
    try {
      await apiDelete(`/api/size-guides?id=${id}`, tid);
      toast.success("Guía eliminada");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo eliminar");
    }
  };

  const columns: DataTableColumn<SizeGuide>[] = [
    {
      key: "nombre",
      header: "Guía",
      render: (g) => (
        <div>
          <p className="text-sm font-medium">{g.name}</p>
          <p className="text-xs text-muted-foreground">
            {g.content.columns.join(" · ")} ({g.unit})
          </p>
        </div>
      ),
    },
    {
      key: "productos",
      header: "Productos asociados",
      render: (g) => <Badge variant={g.productCount ? "default" : "secondary"}>{g.productCount ?? 0}</Badge>,
    },
    {
      key: "acciones",
      header: "Acciones",
      className: "text-right",
      render: (g) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => startEdit(g)} title="Editar">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => requestDelete(g)} title="Eliminar">
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div>
        <CrudShellHeader title="Guías de talles" description="Tablas de medidas reutilizables por múltiples productos" />
        <Loading variant="list" rows={4} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CrudShellHeader
        title="Guías de talles"
        description="Tablas de medidas reutilizables por múltiples productos — opcional, no todos los productos necesitan una."
        actions={
          <Button size="sm" className="gap-1.5" onClick={() => { setShowForm((v) => !v); if (editingId) resetForm(); }}>
            <Plus className="h-4 w-4" /> Nueva guía
          </Button>
        }
      />

      {showForm && (
        <Panel
          title={editingId ? "Editar guía" : "Nueva guía"}
          actions={
            <button type="button" onClick={resetForm} className="text-muted-foreground hover:text-foreground" title="Cancelar">
              <X className="h-4 w-4" />
            </button>
          }
        >
          {editingId && (
            <p className="mb-3 text-xs text-amber-600">
              Esta guía es compartida — los cambios se reflejan en todos los productos que la usan.
            </p>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Remeras / Hombre" />
            </div>
            <div>
              <Label className="text-xs">Unidad</Label>
              <Input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} placeholder="cm" />
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">Instrucciones (opcional)</Label>
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Medidas tomadas con la prenda extendida" />
          </div>

          <div className="mt-4 overflow-x-auto rounded-lg border border-border/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/30">
                  {form.columns.map((c, ci) => (
                    <th key={ci} className="p-2">
                      <div className="flex items-center gap-1">
                        <Input
                          value={c}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, columns: f.columns.map((x, i) => (i === ci ? e.target.value : x)) }))
                          }
                          placeholder={`Columna ${ci + 1}`}
                          className="h-8 text-xs font-semibold"
                        />
                        {form.columns.length > 1 && (
                          <button type="button" onClick={() => removeColumn(ci)} title="Quitar columna">
                            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="p-2 w-10">
                    <Button variant="ghost" size="sm" onClick={addColumn} title="Agregar columna">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {form.rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-border/30 last:border-0">
                    {row.map((v, ci) => (
                      <td key={ci} className="p-2">
                        <Input
                          value={v}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              rows: f.rows.map((r, i) => (i === ri ? r.map((x, j) => (j === ci ? e.target.value : x)) : r)),
                            }))
                          }
                          className="h-8 text-xs"
                        />
                      </td>
                    ))}
                    <td className="p-2">
                      {form.rows.length > 1 && (
                        <button type="button" onClick={() => removeRow(ri)} title="Quitar fila">
                          <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" className="mt-2 gap-1.5" onClick={addRow}>
            <Plus className="h-3.5 w-3.5" /> Agregar fila
          </Button>

          <div className="mt-4 flex gap-2">
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button size="sm" variant="ghost" onClick={resetForm}>
              Cancelar
            </Button>
          </div>
        </Panel>
      )}

      <Panel title="Guías existentes">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre..."
          className="mb-3 max-w-sm"
        />
        <DataTable
          columns={columns}
          rows={filtered}
          getRowKey={(g) => g.id}
          emptyState={
            <CrudEmptyState
              icon={<Ruler className="h-8 w-8" />}
              title="Todavía no creaste ninguna guía de talles"
              description="Creá una desde acá o directamente desde el editor de un producto."
            />
          }
        />
      </Panel>

      <AlertDialog open={!!deleteBlocked} onOpenChange={(open) => !open && setDeleteBlocked(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No se puede eliminar "{deleteBlocked?.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              Está en uso por {deleteBlocked?.productCount} producto{deleteBlocked?.productCount === 1 ? "" : "s"}. Desasocialá esos
              productos primero desde su editor (sección "Guía de talles" → "Sin guía") y volvé a intentar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDeleteBlocked(null)}>Entendido</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
